// MF-67 — Create/Edit Journal Zone (Maestro-managed school area). Name (its internal code is derived
// from it on create and never shown) + optional
// map pin (Leaflet/OpenStreetMap, no API key): click the map or type the coordinates.
import { useEffect, useState } from 'react'
import { configPath } from '../paths'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Box, Button, CircularProgress, TextField } from '@mui/material'
import MapPicker from '../components/MapPicker'
import FormCard from '../components/FormCard'
import FormField from '../components/FormField'
import FormSection from '../components/FormSection'
import NumberField from '../components/NumberField'
import { inputSx } from '../components/formStyles'
import { getZone, createZone, updateZone } from '../../../../services/journal/zones.service'

const slug = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

const ZoneForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const schoolGeo = useSelector((state) => state?.tenant?.tenant?.geo)

  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [lat, setLat] = useState('')
  const [lng, setLng] = useState('')
  const [saving, setSaving] = useState(false)
  const [loadingData, setLoadingData] = useState(Boolean(id))

  useEffect(() => {
    if (!id) return
    getZone(id)
      .then(({ data }) => {
        if (!data) return
        setName(data.name || '')
        setCode(data.code || '')
        setLat(data.lat ?? '')
        setLng(data.lng ?? '')
      })
      .catch(() => {})
      .finally(() => setLoadingData(false))
  }, [id])

  useEffect(() => {
    if (!id) setCode(slug(name))
  }, [name, id])

  const canSave = name.trim() && !saving
  const hasPin = lat !== '' && lng !== ''

  const onSave = async () => {
    setSaving(true)
    try {
      const payload = {
        name: name.trim(),
        code: code || slug(name),
        lat: lat === '' ? null : Number(lat),
        lng: lng === '' ? null : Number(lng)
      }
      if (id) await updateZone(id, payload)
      else await createZone(payload)
      enqueueSnackbar('Zone saved', { variant: 'success' })
      navigate(configPath('zones'))
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error?.description || 'Could not save zone', {
        variant: 'error'
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      {loadingData ? (
        <div className='flex justify-center' style={{ padding: '40px 0' }}>
          <CircularProgress />
        </div>
      ) : (
        <div style={{ padding: '24px 8px', maxWidth: 880, margin: '0 auto' }}>
          <FormCard
            title={id ? 'Zone details' : 'New zone'}
            description='An area of the school where events happen. Zones power the "by location" reports.'
            footer={
              <>
                <Button color='inherit' onClick={() => navigate(configPath('zones'))}>
                  Cancel
                </Button>
                <Button variant='contained' disabled={!canSave} onClick={onSave}>
                  {saving ? 'Saving…' : 'Save zone'}
                </Button>
              </>
            }
          >
            <Box className='grid' style={{ gap: 20 }} sx={{ gridTemplateColumns: '1fr' }}>
              <FormField label='Name' required htmlFor='zone-name' hint='e.g. Yard, Gym, Bus bay'>
                <TextField
                  id='zone-name'
                  size='small'
                  fullWidth
                  autoFocus={!id}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  sx={inputSx}
                />
              </FormField>
            </Box>

            <FormSection
              title='Map pin'
              description='Optional. Click the map to drop the pin, or type the coordinates.'
              action={
                hasPin ? (
                  <Button
                    size='small'
                    color='inherit'
                    onClick={() => {
                      setLat('')
                      setLng('')
                    }}
                  >
                    Clear pin
                  </Button>
                ) : null
              }
            >
              <MapPicker
                value={{ lat: lat === '' ? null : Number(lat), lng: lng === '' ? null : Number(lng) }}
                center={schoolGeo}
                onChange={(pt) => {
                  setLat(pt.lat)
                  setLng(pt.lng)
                }}
                height={300}
              />
              <Box
                className='grid'
                style={{ gap: 20 }}
                sx={{ gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}
              >
                <FormField label='Latitude' htmlFor='zone-lat'>
                  <NumberField
                    id='zone-lat'
                    size='small'
                    fullWidth
                    allowDecimal
                    min={-90}
                    max={90}
                    value={lat}
                    onChange={(e) => setLat(e.target.value)}
                    sx={inputSx}
                  />
                </FormField>
                <FormField label='Longitude' htmlFor='zone-lng'>
                  <NumberField
                    id='zone-lng'
                    size='small'
                    fullWidth
                    allowDecimal
                    min={-180}
                    max={180}
                    value={lng}
                    onChange={(e) => setLng(e.target.value)}
                    sx={inputSx}
                  />
                </FormField>
              </Box>
            </FormSection>
          </FormCard>
        </div>
      )}
    </>
  )
}

export default ZoneForm
